import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Printer, Send, CheckCircle2, Wheat, Banknote, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const cls = 'w-full px-3 py-2.5 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none font-body text-sm';
const fmtRp = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n || 0);

const PRESET_HARGA = [10000, 11000, 12000, 13000, 14000, 15000, 16000, 17000];
const EMPTY = { nama_kk: '', phone: '', alamat: '', jumlah_jiwa: 1, jenis_pembayaran: 'uang', harga_beras_per_kg: 0, has_infak: false, nominal_infak: 0, tanggal: new Date().toISOString().slice(0, 10) };

function BuktiModal({ txn, onClose }) {
  const [qrUrl, setQrUrl] = useState('');
  const printRef = useRef();

  useEffect(() => {
    if (txn?.id) {
      setQrUrl(`${API_URL}/api/zakat-ramadan/transactions/${txn.id}/qr`);
    }
  }, [txn]);

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open('', '_blank', 'width=500,height=700');
    win.document.write(`<html><head><title>Bukti Zakat</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; margin: 0; padding: 16px; }
        .header { text-align: center; border-bottom: 2px solid #065F46; padding-bottom: 10px; margin-bottom: 12px; }
        .title { font-size: 16px; font-weight: bold; color: #065F46; }
        .row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px dashed #e5e7eb; }
        .label { color: #6b7280; }
        .value { font-weight: 600; }
        .total { font-size: 14px; font-weight: bold; color: #065F46; margin-top: 8px; text-align: right; }
        img { max-width: 80px; display: block; margin: 8px auto; }
        @media print { body { margin: 0; } }
      </style></head><body>${content}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const handleWhatsApp = () => {
    const isBerasType = txn.jenis_pembayaran === 'beras';
    const msg = encodeURIComponent(
      `*BUKTI ZAKAT FITRAH*\n` +
      `No: ${txn.nomor_transaksi}\n` +
      `Muzakki: ${txn.nama_kk}\n` +
      `Jiwa: ${txn.jumlah_jiwa}\n` +
      `Zakat: ${isBerasType ? `${txn.total_zakat_beras} kg beras` : fmtRp(txn.total_zakat_uang)}\n` +
      (txn.has_infak ? `Infak: ${fmtRp(txn.nominal_infak)}\n` : '') +
      `Amil: ${txn.nama_amil}\n` +
      `Tgl: ${txn.tanggal}\n\n` +
      `_masjidberdaya.id_`
    );
    window.open(`https://wa.me/${txn.phone?.replace(/\D/g, '')}?text=${msg}`, '_blank');
  };

  if (!txn) return null;
  const isBerasType = txn.jenis_pembayaran === 'beras';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-stone-100">
          <h3 className="font-semibold text-stone-800">Bukti Pembayaran</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-stone-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5" ref={printRef}>
          <div className="header text-center mb-4">
            <div className="title text-emerald-800 font-bold text-base">BUKTI ZAKAT FITRAH RAMADAN</div>
            <p className="text-xs text-stone-500 mt-1">{txn.profile?.entity_name || 'masjidberdaya.id'}</p>
          </div>
          {qrUrl && (
            <div className="flex justify-center mb-3">
              <img src={qrUrl} alt="QR Verifikasi" className="w-20 h-20 border border-stone-200 rounded-lg" />
            </div>
          )}
          <div className="space-y-2 text-sm border border-dashed border-stone-200 rounded-xl p-4">
            {[
              ['No. Transaksi', txn.nomor_transaksi],
              ['Tanggal', txn.tanggal],
              ['Nama Muzakki', txn.nama_kk],
              ['No. HP', txn.phone || '-'],
              ['Alamat', txn.alamat || '-'],
              ['Jumlah Jiwa', `${txn.jumlah_jiwa} jiwa`],
              ['Jenis Zakat', isBerasType ? 'Beras' : 'Uang'],
              ['Nilai Zakat', isBerasType ? `${txn.total_zakat_beras} kg beras` : fmtRp(txn.total_zakat_uang)],
              ...(txn.has_infak ? [['Infak', fmtRp(txn.nominal_infak)]] : []),
              ['Nama Amil', txn.nama_amil || '-'],
            ].map(([label, value]) => (
              <div key={label} className="row flex justify-between">
                <span className="label text-stone-500">{label}</span>
                <span className="value text-stone-800 font-medium text-right max-w-[180px]">{value}</span>
              </div>
            ))}
            {!isBerasType && (
              <div className="total text-right text-emerald-800 font-bold pt-1">
                Total: {fmtRp((txn.total_zakat_uang || 0) + (txn.nominal_infak || 0))}
              </div>
            )}
          </div>
          <p className="text-xs text-center text-stone-400 mt-3">masjidberdaya.id — Zakat Ramadan</p>
        </div>
        <div className="flex gap-2 p-4 border-t border-stone-100">
          <button onClick={handlePrint} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-stone-200 text-sm text-stone-700 hover:bg-stone-50">
            <Printer className="w-4 h-4" /> Print
          </button>
          {txn.phone && (
            <button onClick={handleWhatsApp} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm">
              <Send className="w-4 h-4" /> WhatsApp
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ZakatTransaksiPane({ profile, mosqueId }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [bukti, setBukti] = useState(null);
  const [recent, setRecent] = useState([]);

  // Available price options from profile settings
  const availableHarga = (profile?.harga_beras_options?.length > 0)
    ? profile.harga_beras_options
    : PRESET_HARGA;

  const defaultHarga = profile?.harga_beras_per_kg || 15000;

  const openForm = () => {
    setForm({ ...EMPTY, harga_beras_per_kg: defaultHarga });
    setShowForm(true);
  };

  const loadRecent = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/zakat-ramadan/transactions`, { ...cfg, params: { limit: 10 } });
      setRecent(data.data);
    } catch {}
  };

  useEffect(() => { loadRecent(); }, [mosqueId]);

  const calc = () => {
    const jiwa = parseInt(form.jumlah_jiwa) || 0;
    const isBerasType = form.jenis_pembayaran === 'beras';
    const nilaiBerasPerJiwa = profile?.nilai_beras_per_jiwa || 2.5;
    if (isBerasType) {
      const totalZakat = jiwa * nilaiBerasPerJiwa;
      return { jiwa, isBerasType, nilaiPerJiwa: nilaiBerasPerJiwa, totalZakat, infak: form.has_infak ? (parseFloat(form.nominal_infak) || 0) : 0 };
    } else {
      const harga = parseFloat(form.harga_beras_per_kg) || defaultHarga;
      const nilaiPerJiwa = nilaiBerasPerJiwa * harga;
      const totalZakat = jiwa * nilaiPerJiwa;
      return { jiwa, isBerasType, nilaiPerJiwa, harga, totalZakat, infak: form.has_infak ? (parseFloat(form.nominal_infak) || 0) : 0 };
    }
  };

  const { jiwa, isBerasType, nilaiPerJiwa, harga, totalZakat, infak } = calc();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nama_kk.trim()) return toast.error('Nama Muzakki wajib diisi');
    setSaving(true);
    try {
      const { data: txn } = await axios.post(`${API_URL}/api/zakat-ramadan/transactions`, {
        ...form,
        harga_beras_per_kg: isBerasType ? undefined : (parseFloat(form.harga_beras_per_kg) || defaultHarga),
      }, cfg);
      // Fetch full transaction with profile
      const { data: full } = await axios.get(`${API_URL}/api/zakat-ramadan/transactions/${txn.id}`, cfg);
      toast.success(`Transaksi ${txn.nomor_transaksi} berhasil dicatat!`);
      setBukti(full);
      setShowForm(false);
      setForm(EMPTY);
      loadRecent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan transaksi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="zakat-transaksi">
      {bukti && <BuktiModal txn={bukti} onClose={() => setBukti(null)} />}

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-stone-700 text-sm">Bayar Zakat Fitrah</h3>
        <button onClick={openForm} data-testid="tambah-transaksi-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Bayar Zakat Fitrah
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Tanggal</label>
              <input type="date" value={form.tanggal}
                onChange={e => setForm(f => ({ ...f, tanggal: e.target.value }))} className={cls} required />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Nama Kepala Keluarga *</label>
              <input value={form.nama_kk} onChange={e => setForm(f => ({ ...f, nama_kk: e.target.value }))}
                className={cls} placeholder="Nama lengkap KK" required />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">No. HP</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className={cls} placeholder="08xxxxxxxxxx" type="tel" />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Alamat</label>
              <input value={form.alamat} onChange={e => setForm(f => ({ ...f, alamat: e.target.value }))}
                className={cls} placeholder="Alamat tinggal" />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Jumlah Jiwa</label>
              <input type="number" min="1" max="50" value={form.jumlah_jiwa}
                onChange={e => setForm(f => ({ ...f, jumlah_jiwa: parseInt(e.target.value) || 1 }))} className={cls} />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Jenis Pembayaran</label>
              <div className="flex gap-2">
                {[['uang', 'Uang', Banknote], ['beras', 'Beras', Wheat]].map(([val, label, Icon]) => (
                  <button type="button" key={val} onClick={() => setForm(f => ({ ...f, jenis_pembayaran: val, harga_beras_per_kg: val === 'uang' ? defaultHarga : 0 }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-colors ${form.jenis_pembayaran === val ? 'bg-emerald-700 border-emerald-700 text-white' : 'border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Pilihan Harga Beras untuk Uang */}
          {!isBerasType && (
            <div className="bg-amber-50 rounded-xl p-4 space-y-2.5 border border-amber-100">
              <p className="text-xs font-semibold text-amber-800">Pilih Harga Beras yang Dikonsumsi</p>
              <div className="flex flex-wrap gap-2">
                {availableHarga.map(h => (
                  <button type="button" key={h}
                    onClick={() => setForm(f => ({ ...f, harga_beras_per_kg: h }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      form.harga_beras_per_kg === h
                        ? 'bg-amber-600 border-amber-600 text-white'
                        : 'border-amber-300 text-amber-700 hover:bg-amber-100'
                    }`}>
                    Rp {h.toLocaleString('id-ID')}/kg
                  </button>
                ))}
              </div>
              {form.harga_beras_per_kg > 0 && (
                <p className="text-xs text-amber-700">
                  Perhitungan: {profile?.nilai_beras_per_jiwa || 2.5} kg × Rp {form.harga_beras_per_kg.toLocaleString('id-ID')} = <strong>Rp {nilaiPerJiwa.toLocaleString('id-ID')}/jiwa</strong>
                </p>
              )}
            </div>
          )}

          {/* Calculation preview */}
          <div className="bg-emerald-50 rounded-xl p-4 text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-stone-500">Nilai/Jiwa</span>
              <span className="font-medium">
                {isBerasType
                  ? `${profile?.nilai_beras_per_jiwa || 2.5} kg beras`
                  : fmtRp(nilaiPerJiwa)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">Total Zakat ({jiwa} jiwa)</span>
              <span className="font-semibold text-emerald-800">{isBerasType ? `${totalZakat} kg beras` : fmtRp(totalZakat)}</span>
            </div>

            {/* Infak toggle */}
            <div className="flex items-center gap-2 pt-1">
              <input type="checkbox" id="has_infak" checked={form.has_infak}
                onChange={e => setForm(f => ({ ...f, has_infak: e.target.checked }))} className="accent-emerald-600 w-4 h-4" />
              <label htmlFor="has_infak" className="text-stone-600 cursor-pointer">Tambahkan Infak</label>
            </div>
            {form.has_infak && (
              <div>
                <input type="number" min="0" value={form.nominal_infak}
                  onChange={e => setForm(f => ({ ...f, nominal_infak: parseFloat(e.target.value) || 0 }))}
                  className={cls} placeholder="Nominal infak (Rp)" />
              </div>
            )}

            {form.has_infak && (
              <div className="flex justify-between border-t border-emerald-200 pt-2 font-bold text-emerald-800">
                <span>Total Infak</span>
                <span>{fmtRp(infak)}</span>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => setShowForm(false)}
              className="flex-1 py-2.5 rounded-xl border border-stone-200 text-sm text-stone-600 hover:bg-stone-50">Batal</button>
            <button type="submit" disabled={saving} data-testid="simpan-transaksi-btn"
              className="flex-1 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {saving ? 'Menyimpan...' : 'Simpan & Cetak Bukti'}
            </button>
          </div>
        </form>
      )}

      {/* Recent transactions */}
      {recent.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-100 text-sm font-semibold text-stone-700">Transaksi Terbaru</div>
          <div className="divide-y divide-stone-50">
            {recent.map(txn => (
              <div key={txn.id} className="px-5 py-3 flex items-center justify-between hover:bg-stone-50">
                <div>
                  <p className="text-sm font-medium text-stone-800">{txn.nama_kk}</p>
                  <p className="text-xs text-stone-400">{txn.nomor_transaksi} · {txn.tanggal}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-emerald-700">
                    {txn.jenis_pembayaran === 'beras' ? `${txn.total_zakat_beras} kg` : fmtRp(txn.total_zakat_uang)}
                  </p>
                  {txn.has_infak && <p className="text-xs text-rose-500">+{fmtRp(txn.nominal_infak)} infak</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
